import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ApprovalActionModal = ({ isOpen, refund, action, onClose, onConfirm }) => {
  const [refundAmount, setRefundAmount] = useState(refund?.refundAmount || 0);
  const [selectedReason, setSelectedReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  if (!isOpen || !refund) return null;

  const actionConfig = {
    approve_full: {
      title: 'Approve Full Refund',
      icon: 'CheckCircle',
      color: 'success',
      description: 'Approve the full refund amount'
    },
    partial: {
      title: 'Partial Refund',
      icon: 'DollarSign',
      color: 'info',
      description: 'Approve a partial refund amount'
    },
    credit_note: {
      title: 'Issue Credit Note',
      icon: 'FileText',
      color: 'primary',
      description: 'Issue a credit note instead of refund'
    },
    reject: {
      title: 'Reject Refund',
      icon: 'XCircle',
      color: 'destructive',
      description: 'Reject the refund request'
    }
  };

  const config = actionConfig?.[action] || actionConfig?.approve_full;

  const rejectionReasons = [
    'Insufficient documentation',
    'Outside refund policy window',
    'Service was delivered as promised',
    'Customer error',
    'Duplicate request',
    'Other'
  ];

  const handleConfirm = () => {
    onConfirm(action, action === 'partial' ? refundAmount : refund?.refundAmount, selectedReason, internalNotes);
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-lg bg-card rounded-xl elevation-2xl overflow-hidden border border-border">
        <div className="p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-${config?.color}/20`}>
              <Icon name={config?.icon} size={24} className={`text-${config?.color}`} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
                {config?.title}
              </h2>
              <p className="text-sm text-muted-foreground font-body">
                {config?.description}
              </p>
            </div>
          </div>

          {/* Request Summary */}
          <div className="bg-background border border-border rounded-lg p-4 mb-6">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Customer:</span>
                <span className="text-sm font-medium text-foreground">{refund?.customerName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Request ID:</span>
                <span className="text-sm font-mono font-medium text-foreground">{refund?.requestId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Original Amount:</span>
                <span className="text-sm font-semibold text-foreground">₹{refund?.originalAmount?.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Partial Refund Amount Input */}
          {action === 'partial' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Refund Amount *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                <input
                  type="number"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(Number(e?.target?.value))}
                  max={refund?.originalAmount}
                  className="w-full pl-8 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Maximum: ₹{refund?.originalAmount?.toLocaleString()}
              </p>
            </div>
          )}

          {/* Rejection Reason */}
          {action === 'reject' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Rejection Reason *
              </label>
              <select
                value={selectedReason}
                onChange={(e) => setSelectedReason(e?.target?.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">Select a reason</option>
                {rejectionReasons?.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>
          )}

          {/* Internal Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Internal Notes {action === 'reject' ? '*' : '(Optional)'}
            </label>
            <textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e?.target?.value)}
              rows={3}
              placeholder="Add internal notes for audit trail..."
              className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            />
          </div>

          {/* Warning for Rejection */}
          {action === 'reject' && (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md mb-6">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive font-body">
                  Customer will be notified of the rejection with the selected reason.
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={config?.color}
              size="lg"
              onClick={handleConfirm}
              disabled={action === 'reject' && (!selectedReason || !internalNotes)}
              iconName={config?.icon}
              iconPosition="left"
              fullWidth
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalActionModal;